import React, { useState } from "react";
import { Dict, UserExportDTO } from "../../../api/admin/types";
import { escapeCsvField } from "../../../utils/csv";

interface CsvDownloadButtonProps {
  // ISO datetimes for the export window. In compare mode, callers pass the
  // union of period A and period B so the CSV matches everything on screen.
  startDate: string;
  endDate: string;
  // Which export endpoint to hit.
  mode: "agency" | "users";
  filenamePrefix: string;
  disabled?: boolean;
  disabledReason?: string;
}

const AGENCY_COLUMNS: { key: keyof Dict; label: string }[] = [
  { key: "name", label: "Agency Name" },
  { key: "creationDate", label: "Creation Date" },
  { key: "userCount", label: "User Count" },
  { key: "policiesCount", label: "Policies Count" },
  { key: "comparisonsCount", label: "Comparisons Count" },
  { key: "proposalsCount", label: "Proposals Count" },
  { key: "contractReviewsCount", label: "Contract Reviews Count" },
  { key: "gapAnalysisCount", label: "Coverage Gap Analysis Count" },
  { key: "singlePolicyAnalysisCount", label: "Single Policy Analysis Count" },
  { key: "sovCount", label: "SOV Count" },
  { key: "lossRunsCount", label: "Loss Runs Count" },
  { key: "coiCount", label: "COI Count" },
  { key: "chatsCount", label: "Chats Count" },
];

const USER_COLUMNS: { key: keyof UserExportDTO; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "agency_name", label: "Agency Name" },
  { key: "role", label: "Role" },
  { key: "is_disabled", label: "Disabled" },
  { key: "created_at", label: "Created At" },
  { key: "last_activity_timestamp", label: "Last Activity" },
  { key: "policies_count", label: "Policies Count" },
  { key: "comparisons_count", label: "Comparisons Count" },
  { key: "proposals_count", label: "Proposals Count" },
  { key: "contract_reviews_count", label: "Contract Reviews Count" },
  { key: "gap_analysis_count", label: "Coverage Gap Analysis Count" },
  { key: "single_policy_analysis_count", label: "Single Policy Analysis Count" },
  { key: "sov_count", label: "SOV Count" },
  { key: "loss_runs_count", label: "Loss Runs Count" },
  { key: "coi_count", label: "COI Count" },
  { key: "chats_count", label: "Chats Count" },
];

function arrayToCsv(rows: any[], columns: { key: string; label: string }[]) {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((r) =>
    columns
      .map((c) => {
        let v = r[c.key];
        if (
          (c.key === "creationDate" ||
            c.key === "created_at" ||
            c.key === "last_activity_timestamp") &&
          typeof v === "string"
        ) {
          const d = new Date(v);
          if (!isNaN(d.getTime())) v = d.toISOString().replace("T", " ").substring(0, 19);
        }
        return escapeCsvField(v);
      })
      .join(",")
  );
  return [header, ...body].join("\r\n");
}

function triggerDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const CsvDownloadButton: React.FC<CsvDownloadButtonProps> = ({
  startDate,
  endDate,
  mode,
  filenamePrefix,
  disabled,
  disabledReason,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const onClick = async () => {
    if (disabled || isDownloading) return;
    setIsDownloading(true);
    try {
      // Required at call time only; keeps the existing eager-import pattern
      // used by ExportDataPage, which avoids pulling the controller into the
      // initial JS bundle for users who never click Download.
      const AdminController = require("../../../controllers/AdminController").default;
      const fname = `${filenamePrefix}_${startDate.slice(0, 10)}_to_${endDate.slice(0, 10)}.csv`;
      if (mode === "users") {
        const data: UserExportDTO[] = await AdminController.getUserExport(startDate, endDate);
        triggerDownload(arrayToCsv(data, USER_COLUMNS), fname);
      } else {
        const data = await AdminController.getUsageExport(startDate, endDate);
        triggerDownload(arrayToCsv(data, AGENCY_COLUMNS), fname);
      }
    } catch (e) {
      console.error("CSV download failed", e);
      alert("Download failed. See console.");
    } finally {
      setIsDownloading(false);
    }
  };

  const isDisabled = !!disabled || isDownloading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      title={disabled ? disabledReason : undefined}
      style={{
        padding: "8px 14px",
        borderRadius: 6,
        border: "1px solid #d1d5db",
        background: isDisabled ? "#f3f4f6" : "#fff",
        color: isDisabled ? "#9ca3af" : "#1f2937",
        fontSize: 13,
        fontWeight: 500,
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      {isDownloading ? "Preparing CSV…" : "Download CSV"}
    </button>
  );
};

export default CsvDownloadButton;
